import { I18NextPipe } from 'angular-i18next';
import { bytesFrom, formatBytes } from 'mbs-ui-kit/utils/converter';
import { VirtualMachineType } from 'mbs-ui-shared@models/VirtualMachineType';
import HostInfo, { ComputerState, LocationInfo } from 'mbs-ui-shared@models/rmm/HostInfo';
import HostVmOptionInfo from 'mbs-ui-shared@models/rmm/HostVmOptionInfo';
import { virtualMachineDisplay } from 'mbs-ui-shared@utils/pipes/virtual-machine-type-display.pipe';
import moment from 'moment';
import 'moment-duration-format';

type HostInfoRow = { key: string; value: any; visible?: () => boolean };

function getSystemUpdate(uptime: string): string {
  return moment.duration(uptime, 'seconds').format('d [days] HH [hours] mm [minutes]');
}

export function getLocationInfo(location: string): string {
  if (location) {
    const parsedLocation: LocationInfo = JSON.parse(location);
    if (parsedLocation?.country && parsedLocation?.city) return `${parsedLocation.country} ${parsedLocation.city}`;
    if (parsedLocation?.continentName) return parsedLocation.continentName;

    return null;
  }

  return null;
}

export function getExternalIpFromLocation(location: string): string {
  if (location) {
    const parsedLocation: LocationInfo = JSON.parse(location);
    return parsedLocation?.ip ?? null;
  }

  return null;
}

function showVMInfo(vmInfo: HostVmOptionInfo): boolean {
  const visibleVM = new Set([VirtualMachineType.AmazonV1, VirtualMachineType.AmazonV2, VirtualMachineType.Google]);
  return vmInfo && visibleVM.has(vmInfo.enumerator);
}

function getComputerState(state: ComputerState, i18n: I18NextPipe): { class: string; text: string } {
  switch (state) {
    case ComputerState.NoActiveUserSession:
      return { class: 'text-gray-500', text: i18n.transform('shared:rmm-side-panel.pcInfoTab.computerState.noSession') };
    case ComputerState.ScreenIsLocked:
      return { class: 'text-warning', text: i18n.transform('shared:rmm-side-panel.pcInfoTab.computerState.screenLocked') };
    case ComputerState.ScreenIsActive:
      return { class: 'text-success', text: i18n.transform('shared:rmm-side-panel.pcInfoTab.computerState.screenUnlocked') };
    case ComputerState.Unknown:
    default:
      return null;
  }
}

function getGeneralHostInfo(hostData: HostInfo, online: boolean, hid: string): HostInfoRow[] {
  if (!hostData) {
    return [];
  }

  return [
    { key: 'Computer name', value: hostData.computerName },
    { key: 'Last logged in user', value: `${hostData.userDomainName}\\${hostData.userName}` },
    { key: 'Last login time', value: hostData.lastLogin },
    { key: 'Domain', value: hostData.domainName },
    { key: 'IP address', value: hostData.network ? hostData.network.ip4Address : '' },
    { key: 'External IP address', value: getExternalIpFromLocation(hostData.location), visible: () => !!hostData.location },
    {
      key: 'Virtualization',
      value: hostData.vm ? virtualMachineDisplay(hostData.vm.enumerator) : '',
      visible: () => !!hostData.vm
    },
    { key: 'Instance id', value: hostData.vm ? hostData.vm.instanceID : '', visible: () => showVMInfo(hostData.vm) },
    { key: 'Physical memory', value: formatBytes(+hostData.totalPhysicalMemory * bytesFrom('KB')) },
    { key: 'Virtual memory', value: formatBytes(+hostData.totalVirtualMemory * bytesFrom('KB')) },
    { key: 'Processor', value: hostData.cpuName },
    { key: 'Socket count', value: hostData.physicalProcessorsCount },
    { key: 'Operating system', value: hostData.osName },
    { key: 'OS version', value: hostData.version },
    { key: 'OS type', value: hostData.productType },
    { key: 'Platform type', value: hostData.systemType },
    { key: 'Manufactured', value: hostData.manufactured },
    { key: 'Model', value: hostData.model },
    { key: 'Serial number', value: hostData.serialNumber },
    { key: 'System uptime', value: getSystemUpdate(hostData.systemUptime), visible: () => online },
    { key: 'Net Framework version', value: hostData.frameworkVersion },
    { key: 'Time zone', value: hostData.timeZone },
    { key: 'Culture', value: hostData.curCultureName },
    { key: 'Location', value: getLocationInfo(hostData.location), visible: () => !!hostData.location },
    { key: 'HID', value: hid }
  ];
}

export { getComputerState, getGeneralHostInfo, HostInfoRow };
